/**
 * 메뉴 노출 판정 — **서버 `apps/chat-server/src/lib/menu-access.ts` 의 `judgeMenu` 사본** (2026-09-15 · 메뉴 권한 개편 R1+R7).
 *
 * ★★★ 서버와 **한 글자도 다르면 안 된다.** 화면이 「보임」이라 했는데 서버가 감추면 담당자는 없는 버그를 찾는다.
 *     그래서 두 쪽이 **같은 사례표**(`packages/shared-types/fixtures/menu-judge-cases.json`)를 돌린다
 *     (`menu-judge.test.ts`). 한쪽을 바꾸려면 **표부터** 바꾼다.
 *
 * 순서: 카탈로그 끔 → **회사 끔** → **모듈 끔** → 팀 → 본부 → (없으면 회사 설정) → **개인 덮어쓰기**.
 *   · 회사 끔 · 모듈 끔은 팀 · 본부 · 개인 어느 허용도 못 넘는다(판 4 의 「대상 allow 면 되살림」은 없다).
 *   · 팀 없는 직원의 부서 기준은 회사 설정이다 — `unassigned` · `role` 줄은 판정에 안 쓴다.
 *   · 개인 줄은 그 메뉴만 덮어쓴다 — 결과가 달라지면 `applied`, 같으면 `same`, 회사/모듈 끔이 막으면 `blocked`.
 *
 * 옛 사다리(팀 → 부서 → 미배정 · 회사 끔 되살림)는 `lib/menu-matrix.ts` 에 남아 있다 — 새 화면은 쓰지 않는다.
 */

export type Verdict = 'allow' | 'deny' | 'none';

export interface JudgeRule {
  scopeType: string;
  scopeId: string;
  allow: boolean;
}

export interface JudgeViewer {
  /** 부서 기준만 볼 때(부서별 탭)는 null. */
  userId: string | null;
  teamId: string | null;
  divisionId: string | null;
}

export interface JudgeInput {
  catalogEnabled?: boolean;
  /** 회사 설정 줄 — 없으면 null(= 켬). */
  companyEnabled: boolean | null;
  moduleEnabled: boolean;
  /** 그 메뉴에 걸린 줄 전부. */
  rules: readonly JudgeRule[];
  viewer: JudgeViewer;
}

export interface BaseVerdict {
  verdict: Verdict;
  /** 걸린 층. 아무 층에도 안 걸렸으면 `company`(= 회사 설정대로). */
  source: 'team' | 'division' | 'company';
  sourceId: string | null;
}

export type UserState = 'applied' | 'same' | 'blocked';

export interface Judgement {
  visible: boolean;
  reason: 'catalog-off' | 'company-off' | 'module-off' | 'not-allowed' | null;
  base: BaseVerdict;
  user: Verdict;
  userState: UserState | null;
  blockedBy: 'company' | 'module' | null;
  explicitAllow: boolean;
}

const LADDER = ['team', 'division'] as const;

/** 부서 기준(사다리) — 가장 가까운 층에서 나에게 걸리는 줄. 같은 층에 여러 줄이면 하나라도 허용이면 허용. */
export function resolveBase(rules: readonly JudgeRule[], viewer: JudgeViewer): BaseVerdict {
  const mine: Record<(typeof LADDER)[number], string | null> = {
    team: viewer.teamId,
    division: viewer.divisionId,
  };
  for (const scope of LADDER) {
    const target = mine[scope];
    if (!target) continue;
    const hit = rules.filter((r) => r.scopeType === scope && r.scopeId === target);
    if (hit.length === 0) continue;
    return { verdict: hit.some((r) => r.allow) ? 'allow' : 'deny', source: scope, sourceId: target };
  }
  return { verdict: 'none', source: 'company', sourceId: null };
}

export function judgeMenu(input: JudgeInput): Judgement {
  const { rules, viewer } = input;
  const base = resolveBase(rules, viewer);
  const own = viewer.userId ? rules.filter((r) => r.scopeType === 'user' && r.scopeId === viewer.userId) : [];
  const user: Verdict = own.length === 0 ? 'none' : own.some((r) => r.allow) ? 'allow' : 'deny';

  // 제품에 없는 메뉴 — 누구도 되살릴 수 없다.
  if (input.catalogEnabled === false) {
    return { visible: false, reason: 'catalog-off', base, user, userState: null, blockedBy: null, explicitAllow: false };
  }

  const blockedBy: 'company' | 'module' | null =
    input.companyEnabled === false ? 'company' : !input.moduleEnabled ? 'module' : null;
  if (blockedBy) {
    return {
      visible: false,
      reason: blockedBy === 'company' ? 'company-off' : 'module-off',
      base,
      user,
      // 개인 허용은 막혔고(blocked), 개인 차단은 어차피 같은 결과(same)다.
      userState: user === 'none' ? null : user === 'allow' ? 'blocked' : 'same',
      blockedBy,
      explicitAllow: false,
    };
  }

  const baseVisible = base.verdict !== 'deny';
  if (user === 'none') {
    return {
      visible: baseVisible,
      reason: baseVisible ? null : 'not-allowed',
      base,
      user,
      userState: null,
      blockedBy: null,
      explicitAllow: baseVisible && base.verdict === 'allow',
    };
  }
  const visible = user === 'allow';
  return {
    visible,
    reason: visible ? null : 'not-allowed',
    base,
    user,
    userState: visible === baseVisible ? 'same' : 'applied',
    blockedBy: null,
    explicitAllow: visible,
  };
}
